import { type StoreApi, createStore } from "zustand";
import { isChoiceIneligible } from "../isChoiceIneligible";
import { makePermitIntervalsISO } from "../makePermitIntervals";
import {
	type PermitFormData,
	type PermitFormStore,
	type PermitPostData,
	type PortalError,
	type RecursivePermitFormData,
	type StepId,
	type WizardSettings,
	stepIds,
} from "./types";

export const defaultWizardSettings: WizardSettings = {
	startDateTimePageEnabled: false,
	endDateTimePageEnabled: false,
	time: {
		timezone: "",
		startDate: { enabled: false },
		startTime: { enabled: false },
		endDate: { enabled: false },
		endTime: { enabled: false },
		duration: { enabled: false },
		instant: false,
	},
	maxVehicleCount: 1,
	codes: {
		enabled: false,
	},
	isEmailRequired: false,
	isSmsRequired: false,
	isEditPermitDisabled: false,
	isCancelPermitDisabled: false,
	hideFutureParkingOptions: false,
	additionalInformation: {
		enabled: false,
		fields: [],
	},
	choices: [],
	isTermsEnabled: false,
};

export const getStepsFromWizardSettings = ({
	wizardSettings,
	parkingChoiceId,
	isParkingTimeStepForced = false,
}: {
	wizardSettings: WizardSettings;
	parkingChoiceId?: number;
	isParkingTimeStepForced?: boolean;
}) => {
	const parkingChoice = parkingChoiceId
		? wizardSettings.choices?.find((choice) => choice.permitTypeId === parkingChoiceId)
		: wizardSettings.choices?.[0];

	const locationChoiceEnabled = wizardSettings.choices.some((choice) => choice.locationChoiceEnabled);
	const hasTimeChoice = wizardSettings.choices.some(
		(choice) =>
			(choice.time.startDate.enabled ||
				choice.time.startTime.enabled ||
				choice.time.endDate.enabled ||
				choice.time.endTime.enabled) &&
			!choice.time.intervals,
	);

	const hasIneligibleChoice = wizardSettings.choices.some(isChoiceIneligible);

	return [
		(isParkingTimeStepForced ||
			wizardSettings.choices.length > 1 ||
			locationChoiceEnabled ||
			hasTimeChoice ||
			hasIneligibleChoice) &&
			stepIds.parkingTime,
		stepIds.parkingDetails,
		wizardSettings.additionalInformation.enabled && stepIds.additionalInfo,
		wizardSettings.codes.enabled && stepIds.code,
		wizardSettings.isTermsEnabled && stepIds.terms,
		parkingChoice?.payments.enabled && stepIds.payment,
		stepIds.confirmation,
	].filter((step): step is StepId => Boolean(step));
};

export type CreatePermitStoreOptions = {
	formData?: Pick<PermitFormData, "vehicles" | "email">;
	wizardSettings?: WizardSettings;
	isParkingTimeStepForced?: boolean;
};

export const createPermitStore = (options: CreatePermitStoreOptions = {}): StoreApi<PermitFormStore> => {
	const wizardSettings = options.wizardSettings ?? defaultWizardSettings;

	const onlyPermitChoice = wizardSettings.choices.length === 1 ? wizardSettings.choices?.[0] : undefined;

	const hasTimeChoice = onlyPermitChoice
		? (onlyPermitChoice.time.startDate.enabled ||
				onlyPermitChoice.time.startTime.enabled ||
				onlyPermitChoice.time.endDate.enabled ||
				onlyPermitChoice.time.endTime.enabled) &&
			!onlyPermitChoice.time.intervals
		: false;

	const initialFormData: RecursivePermitFormData = {
		prev: undefined,
		...options.formData,
		...(onlyPermitChoice && !options.isParkingTimeStepForced
			? {
					permitTypeId: onlyPermitChoice?.permitTypeId,
					parkingChoiceId: onlyPermitChoice?.permitTypeId,
					permitIntervals: !hasTimeChoice
						? makePermitIntervalsISO(onlyPermitChoice.time, onlyPermitChoice.time.timezone)
						: undefined,
				}
			: {}),
	};

	const steps = getStepsFromWizardSettings({
		wizardSettings,
		isParkingTimeStepForced: options.isParkingTimeStepForced,
	});
	const activeStep = steps[0];
	if (!activeStep) {
		throw new Error(`No steps found for wizard settings ${JSON.stringify(wizardSettings)}`);
	}

	return createStore<PermitFormStore>((set, get) => ({
		formData: initialFormData,
		wizardSettings,
		steps,
		activeStep,
		error: undefined,
		isProcessingPermit: false,
		actions: {
			setError: (error?: PortalError) => set((state) => ({ ...state, error })),
			get goNext() {
				return get().steps.indexOf(get().activeStep) === get().steps.length - 1
					? undefined
					: (updater: (data: PermitFormData) => Partial<PermitFormData>) => {
							// Step before confirmation screen
							let hasReachedEnd = get().steps.indexOf(get().activeStep) === get().steps.length - 2;
							let hasPaymentsStep = false;
							set((state) => {
								let updatedData = updater(state.formData);
								const parkingChoiceChanged =
									updatedData.parkingChoiceId !== undefined &&
									state.formData.parkingChoiceId !== undefined &&
									updatedData.parkingChoiceId !== state.formData.parkingChoiceId;

								if (parkingChoiceChanged) {
									updatedData = updater(initialFormData);
								}

								const wizardSettings = state.wizardSettings;
								const parkingChoice = updatedData.parkingChoiceId
									? state.wizardSettings.choices.find(
											(x) => x.permitTypeId === updatedData.parkingChoiceId,
										)
									: undefined;

								const steps = getStepsFromWizardSettings({
									wizardSettings,
									parkingChoiceId: updatedData.parkingChoiceId,
									isParkingTimeStepForced: options.isParkingTimeStepForced,
								});

								hasPaymentsStep = parkingChoice?.payments.enabled ?? false;
								hasReachedEnd = steps.indexOf(get().activeStep) === steps.length - 2;
								const newActiveStep =
									steps[Math.min(steps.length - 1, steps.indexOf(state.activeStep) + 1)];

								return {
									...state,
									formData: {
										...updatedData,
										prev: {
											...updatedData,
											prev: state.formData.prev,
										},
									},
									activeStep: newActiveStep,
									steps,
									error: undefined,
								};
							});

							return {
								hasReachedEnd,
								hasPaymentsStep,
							};
						};
			},
			setIsProcessingPermit: (isProcessingPermit: boolean) => set((state) => ({ ...state, isProcessingPermit })),
			goToStep: (step) =>
				set((state) => {
					if (!state.steps.includes(step)) {
						return state;
					}
					return {
						...state,
						activeStep: step,
						error: undefined,
					};
				}),
			getPermitPostData: (): PermitPostData => {
				const { ...formData } = get().formData;
				return {
					permitTypeId: formData.permitTypeId!,
					parkingChoiceId: formData.parkingChoiceId!,
					rawIntervals:
						formData.permitIntervals?.map((interval) => ({
							startDateTimeISO: interval.startDateTimeISO!,
							endDateTimeISO: interval.endDateTimeISO ?? undefined,
						})) ?? [],
					locationIds: formData.locationIds ?? [],
					vehicles: formData.vehicles ?? [],
					fields: formData.fields ?? [],
					code: formData.code,
					email: formData.email || undefined,
					phoneNumber: formData.phoneNumber || undefined,
					parkerTermsVersionIds: formData.parkerTermsVersionIds,
				};
			},
			prefillAccountData: ({ email, vehicles }: Pick<PermitFormData, "vehicles" | "email">) =>
				set((state) => {
					const hasEnteredVehicle = state.formData.vehicles?.some((vehicle) => vehicle.plate) ?? false;
					return {
						...state,
						formData: {
							...state.formData,
							email: state.formData.email || email,
							vehicles: hasEnteredVehicle || !vehicles?.length ? state.formData.vehicles : vehicles,
						},
					};
				}),
			setAcceptedTerms: (value: Pick<PermitFormData, "parkerTermsVersionIds">) =>
				set((state) => ({
					...state,
					formData: {
						...state.formData,
						parkerTermsVersionIds: value.parkerTermsVersionIds,
						prev: {
							...state.formData.prev,
							parkerTermsVersionIds: state.formData.parkerTermsVersionIds,
						},
					},
					error: undefined,
				})),
		},
	}));
};
